// Discord message content

// Demo messages - sequence of user messages for the auto demo
export const demoMessages = [
  "What are the latest League of Legends patch changes since our last session?",
  "Can you play some music while I game?",
  "Can you create a chart comparing NVIDIA and ETH price history over the past 2 years?",
  "Let's create a poll for what game we should play tonight",
  "Can you set a reminder for our gaming session at 8pm?",
  "What was the status of our D&D campaign from last weekend?",
  "Can you create tournament brackets for the players online right now?"
];

// Predefined tool responses for each demo message
export const demoResponses: Record<string, {tool: string, input: any, output: any}> = {
  "What are the latest League of Legends patch changes since our last session?": {
    tool: 'search_game_news',
    input: { 
      query: "League of Legends patch notes", 
      since_date: "2 weeks ago"
    },
    output: { 
      results: [
        { 
          title: "League of Legends Patch 14.12 Notes", 
          url: "https://example.com/lol-patch-14-12", 
          source: "Riot Games",
          highlights: [
            "Champion Updates: Akali and Samira received buffs, while Sett and Kha'Zix were nerfed",
            "New Void-themed skins for Lux, Yasuo, and Kai'Sa",
            "ARAM balance adjustments for underperforming champions"
          ] 
        }, 
        { 
          title: "League of Legends Mid-Season Update", 
          url: "https://example.com/lol-midseason", 
          source: "LoL Esports",
          highlights: [
            "Dragon soul mechanics reworked for more strategic diversity",
            "Jungle pathing changes to promote early game interaction",
            "Vision system updates with new ping functionality"
          ] 
        }
      ] 
    }
  },
  "Can you play some music while I game?": {
    tool: 'play_music',
    input: { 
      query: "gaming mix", 
      type: "playlist" 
    },
    output: { 
      status: "now_playing", 
      track: "Epic Gaming Mix", 
      artist: "Various Artists", 
      queue_position: 1, 
      requester: "Username" 
    }
  },
  "Can you create a chart comparing NVIDIA and ETH price history over the past 2 years?": {
    tool: 'generate_price_chart',
    input: { 
      assets: ["NVDA", "ETH"],
      timeframe: "2 years",
      chart_type: "line"
    },
    output: { 
      chart_id: "chart_34567",
      chart_title: "NVIDIA vs ETH Price Comparison (2-Year)",
      chart_image_url: "https://placehold.co/600x400/2f3136/5865F2?text=NVIDIA+vs+ETH+Chart",
      assets: [
        {
          symbol: "NVDA", 
          name: "NVIDIA Corporation", 
          current_price: 950.25, 
          percent_change_2y: 285.4,
          price_points: [
            { date: "2022-07-15", price: 246.53 },
            { date: "2022-10-15", price: 130.21 },
            { date: "2023-01-15", price: 178.39 },
            { date: "2023-04-15", price: 271.44 },
            { date: "2023-07-15", price: 425.76 },
            { date: "2023-10-15", price: 484.19 },
            { date: "2024-01-15", price: 577.92 },
            { date: "2024-04-15", price: 881.41 },
            { date: "2024-07-15", price: 950.25 }
          ]
        },
        {
          symbol: "ETH", 
          name: "Ethereum", 
          current_price: 3215.65, 
          percent_change_2y: 72.8,
          price_points: [
            { date: "2022-07-15", price: 1862.21 },
            { date: "2022-10-15", price: 1287.47 },
            { date: "2023-01-15", price: 1564.89 },
            { date: "2023-04-15", price: 1978.52 },
            { date: "2023-07-15", price: 1863.44 },
            { date: "2023-10-15", price: 1723.56 },
            { date: "2024-01-15", price: 2505.74 },
            { date: "2024-04-15", price: 3042.18 },
            { date: "2024-07-15", price: 3215.65 }
          ]
        }
      ],
      analysis: "NVIDIA has significantly outperformed Ethereum over the past 2 years with a 285.4% increase compared to ETH's 72.8%. Both assets saw major gains in early 2024, but NVIDIA's growth accelerated dramatically due to AI demand.",
      timestamp: new Date().toISOString() 
    }
  },
  "Let's create a poll for what game we should play tonight": {
    tool: 'create_poll',
    input: { 
      question: "What game should we play tonight?", 
      options: ["Apex Legends", "Valorant", "Minecraft", "Among Us"] 
    },
    output: { 
      poll_id: "poll_12345", 
      question: "What game should we play tonight?", 
      options: ["Apex Legends", "Valorant", "Minecraft", "Among Us"], 
      status: "created" 
    }
  },
  "Can you set a reminder for our gaming session at 8pm?": {
    tool: 'set_reminder',
    input: { 
      reminder_text: "Gaming session", 
      time_offset: "8pm", 
      target: "channel" 
    },
    output: { 
      reminder_id: "rem_67890", 
      reminder_text: "Gaming session", 
      remind_at: (() => {
        const today = new Date();
        today.setHours(20, 0, 0, 0);
        return today.toISOString();
      })(), 
      status: "set" 
    }
  },
  "What was the status of our D&D campaign from last weekend?": {
    tool: 'retrieve_campaign_status',
    input: { 
      campaign_name: "Curse of Strahd", 
      session_date: "last weekend"
    },
    output: { 
      campaign_name: "Curse of Strahd",
      session_number: 8,
      session_date: (() => {
        const lastWeekend = new Date();
        lastWeekend.setDate(lastWeekend.getDate() - (lastWeekend.getDay() + 6) % 7);
        return lastWeekend.toISOString().split('T')[0];
      })(),
      party_location: "Vallaki",
      party_members: [
        { name: "Alaric", class: "Paladin", level: 6, status: "Healthy" },
        { name: "Sylas", class: "Wizard", level: 6, status: "Low health" },
        { name: "Vex", class: "Ranger", level: 6, status: "Healthy" },
        { name: "Thorne", class: "Cleric", level: 6, status: "Cursed" }
      ],
      quest_status: "The party found the Tome of Strahd and is searching for the Sunsword. They've made an ally of Ezmerelda and are preparing to enter the Amber Temple.",
      dm_notes: "Next session will begin with the ambush at the crossroads by Strahd's minions."
    }
  },
  "Can you create tournament brackets for the players online right now?": {
    tool: 'generate_tournament_brackets',
    input: { 
      tournament_name: "Weekly Gaming Tournament",
      game: "Valorant",
      format: "Single elimination"
    },
    output: { 
      tournament_id: "t_98765",
      tournament_name: "Weekly Gaming Tournament",
      game: "Valorant",
      format: "Single elimination",
      start_time: (() => {
        const startTime = new Date();
        startTime.setHours(startTime.getHours() + 1);
        return startTime.toISOString();
      })(),
      participants: [
        { name: "SteveR", seed: 1, status: "online" },
        { name: "JaneD", seed: 2, status: "idle" },
        { name: "AlexK", seed: 3, status: "online" },
        { name: "MikeP", seed: 4, status: "online" },
        { name: "Sara_23", seed: 5, status: "online" },
        { name: "DarkKnight", seed: 6, status: "online" },
        { name: "ProGamer99", seed: 7, status: "idle" },
        { name: "NoobMaster", seed: 8, status: "online" }
      ],
      brackets: [
        { round: 1, matches: [
          { id: 1, player1: "SteveR", player2: "NoobMaster", time: null },
          { id: 2, player1: "JaneD", player2: "ProGamer99", time: null },
          { id: 3, player1: "AlexK", player2: "DarkKnight", time: null },
          { id: 4, player1: "MikeP", player2: "Sara_23", time: null }
        ]},
        { round: 2, matches: [
          { id: 5, player1: "TBD", player2: "TBD", time: null },
          { id: 6, player1: "TBD", player2: "TBD", time: null }
        ]},
        { round: 3, matches: [
          { id: 7, player1: "TBD", player2: "TBD", time: null }
        ]}
      ]
    }
  }
};

// Initial responses for each tool type
export const initialResponses: Record<string, string> = {
  search_game_news: "Let me check for the latest League of Legends patch changes since your last session.",
  play_music: "Sure thing! I'll start a gaming playlist for you.",
  generate_price_chart: "I'll create a price comparison chart for NVIDIA and Ethereum over the past 2 years.",
  create_poll: "Great idea! I'll set up a poll for tonight's gaming session.",
  set_reminder: "I'll set a reminder for your gaming session at 8pm.",
  retrieve_campaign_status: "Let me check the notes from your last D&D session.",
  generate_tournament_brackets: "I'll create tournament brackets for the players currently online."
};

// Random initial responses for non-demo messages
export const randomInitialResponses = [
  "Got it! Let me handle that.",
  "Okay, I can help with that request.",
  "Working on it now.",
  "Let me process that for you.",
  "Understood. I'll start working on that."
];

// Response templates for each tool type
export const responseTemplates: Record<string, ResponseTemplate> = {
  search_game_news: {
    header: "**League of Legends Patch Changes**",
    intro: `Here are the latest patch changes in League of Legends since your last session:`,
    content: `\n\n{results}`,
    conclusion: `\n\nThese changes might affect your gameplay strategy. Let me know if you want more details on any specific update!`
  },
  play_music: {
    header: "**Now Playing**",
    intro: `I've started playing '{track}' in the voice channel.`,
    content: ``,
    conclusion: `\n\nEnjoy the gaming vibes! Let me know if you want to skip or add more songs.`
  },
  generate_price_chart: {
    header: `**Price Comparison Chart**`,
    intro: `Here's the chart comparing NVIDIA and ETH over the past 2 years:`,
    content: `\n\n{chart_image_url}\n\n**Performance Summary:**\n* **NVIDIA:** {assets[0].current_price} USD (↑ {assets[0].percent_change_2y}% in 2 years)\n* **Ethereum:** {assets[1].current_price} USD (↑ {assets[1].percent_change_2y}% in 2 years)`,
    conclusion: `\n\n**Analysis:** {analysis}\n\n*Data fetched at {timestamp}*`
  },
  create_poll: {
    header: "**Poll Created!**",
    intro: `I've created the poll:`,
    content: `\n\n**{question}**\n{options}`,
    conclusion: `\n\nLet the voting begin!`
  },
  set_reminder: {
    header: "**Reminder Set**",
    intro: `Okay, I'll remind the channel about the '{reminder_text}' at 8pm tonight.`,
    content: ``,
    conclusion: `\n\nYou can manage your reminders with \`/reminders list\`.`
  },
  retrieve_campaign_status: {
    header: "**D&D Campaign Status**",
    intro: `I found the notes from your last {campaign_name} session on {session_date}:`,
    content: `\n\n**Session:** #{session_number}\n**Location:** {party_location}\n\n**Party Status:**\n{party_members}\n\n**Current Quest:**\n{quest_status}`,
    conclusion: `\n\nThe DM notes that next session will begin with {dm_notes}`
  },
  generate_tournament_brackets: {
    header: "**Tournament Brackets Generated!**",
    intro: `Here are the brackets for the {tournament_name} ({game}):`,
    content: `\n\n**Format:** {format}\n**Start Time:** {start_time}\n\n**Round 1 Matches:**\n{brackets}`,
    conclusion: `\n\nGood luck to all participants! Use \`/tournament view\` for more details and match updates.`
  }
};

// Interface for response templates
export interface ResponseTemplate {
  header: string;
  intro: string;
  content: string;
  conclusion: string;
}
